/**
 * Conformance Phase 3 — `selfCheck` for @norkron/web-offline-store.
 *
 * Headless data library — no rendered DOM surface — so the selfCheck is a
 * registered no-op (mirrors `@norkron/web-csrf`). The lib still appears in
 * `audit()` reports for completeness and reserves the slot for opportunistic
 * findings (e.g. detecting consumers that ship unencrypted PII stores once a
 * DOM marker convention exists).
 *
 * Contract (per FR §4): pure, bounded, idempotent, no throws.
 *
 * @example
 * ```ts
 * import { selfCheck } from '@norkron/web-offline-store'
 * const findings = selfCheck(document.body) // → []
 * ```
 */
import { parseSelfCheckOptions } from './validate-self-check'

export type ConformanceSeverity = 'critical' | 'major' | 'minor' | 'info'

/** Structural finding emitted by the web-offline-store selfCheck. */
export interface SelfCheckFinding {
	libId: string
	code: string
	category:
		| 'theme'
		| 'stylesheet'
		| 'a11y'
		| 'layout'
		| 'i18n'
		| 'cardinality'
		| 'identity'
		| 'schema'
		| 'flow'
		| 'deprecation'
		| 'perf'
	severity: ConformanceSeverity
	message: string
	selector?: string
	suggestedFix?: string
	extras?: Record<string, unknown>
}

/** Options for selfCheck. */
export interface SelfCheckOptions {
	budgetMs?: number
	minSeverity?: ConformanceSeverity
	locale?: string
}

const SEVERITY_RANK: Record<ConformanceSeverity, number> = {
	info: 0,
	minor: 1,
	major: 2,
	critical: 3,
}

function meetsFloor(finding: SelfCheckFinding, floor: ConformanceSeverity): boolean {
	return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor]
}

/**
 * Run the web-offline-store self-check.
 *
 * @example
 * ```ts
 * import { selfCheck } from '@norkron/web-offline-store'
 * selfCheck(document.body, { minSeverity: 'major' })
 * ```
 */
export function selfCheck(root: Element, opts?: SelfCheckOptions): SelfCheckFinding[] {
	const safeOpts = parseSelfCheckOptions(opts)
	const findings: SelfCheckFinding[] = []
	try {
		void root
	} catch (err) {
		findings.push({
			libId: '@norkron/web-offline-store',
			code: 'OFFLINE_STORE_SELFCHECK_INTERNAL_ERROR',
			category: 'identity',
			severity: 'info',
			message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`,
		})
	}
	return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity))
}
